import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TrophyIcon } from '@heroicons/react/24/outline';
import LeaderboardEntry from '../leaderboard/LeaderboardEntry';
import LeaderboardFilters, { LeaderboardMode, TimePeriod, RushDuration } from './LeaderboardFilters';
import { leaderboardAPI } from '../../services/api';

interface LeaderboardEntryData {
  rank: number;
  user_id: string;
  username: string;
  score: number;
  achieved_at: string;
  icon_url?: string | null;
  icon_key?: string | null;
}

interface LeaderboardSectionProps {
  currentUserId?: string;
}

interface LeaderboardResponseData {
  entries: LeaderboardEntryData[];
  user_entry?: LeaderboardEntryData | null;
  adjacent_entries?: LeaderboardEntryData[];
  user_rank?: number | null;
}

const LeaderboardSection: React.FC<LeaderboardSectionProps> = ({ currentUserId }) => {
  const [mode, setMode] = useState<LeaderboardMode>('rating');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all_time');
  const [rushDuration, setRushDuration] = useState<RushDuration>(10);
  const [entries, setEntries] = useState<LeaderboardEntryData[]>([]);
  const [userEntry, setUserEntry] = useState<LeaderboardEntryData | null>(null);
  const [adjacentEntries, setAdjacentEntries] = useState<LeaderboardEntryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLeaderboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUserEntry(null);
    setAdjacentEntries([]);
    try {
      let response: LeaderboardResponseData;
      if (mode === 'rating') {
        response = await leaderboardAPI.getRatingLeaderboard(timePeriod);
      } else if (mode === 'survival') {
        response = await leaderboardAPI.getSurvivalLeaderboard(timePeriod);
      } else {
        response = await leaderboardAPI.getRushLeaderboard(rushDuration, timePeriod);
      }
      setEntries(response.entries ?? []);
      setUserEntry(response.user_entry ?? null);
      setAdjacentEntries(response.adjacent_entries ?? []);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
      setError('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [mode, timePeriod, rushDuration]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  const getModeLabel = () => {
    if (mode === 'rating') return 'Rating Leaderboard';
    if (mode === 'survival') return 'Survival Mode Leaderboard';
    return `Rush Mode Leaderboard (${rushDuration} min)`;
  };

  // Find if current user is in the leaderboard
  const outsideTopTen = userEntry ? userEntry.rank > 10 : false;

  const surroundingEntries = useMemo(() => {
    if (!outsideTopTen || !userEntry) {
      return [] as LeaderboardEntryData[];
    }

    const combined = [...adjacentEntries];
    if (!combined.some(entry => entry.user_id === userEntry.user_id)) {
      combined.push(userEntry);
    }

    const uniqueByUser = new Map<string, LeaderboardEntryData>();
    combined.forEach((entry) => {
      uniqueByUser.set(entry.user_id, entry);
    });

    return Array.from(uniqueByUser.values()).sort((a, b) => a.rank - b.rank);
  }, [adjacentEntries, outsideTopTen, userEntry]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Global Leaderboard</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Top performers across all users
        </p>
      </div>

      <div className="p-6">
        {/* Filters */}
        <div className="mb-6">
          <LeaderboardFilters
            mode={mode}
            timePeriod={timePeriod}
            rushDuration={rushDuration}
            onModeChange={setMode}
            onTimePeriodChange={setTimePeriod}
            onRushDurationChange={setRushDuration}
          />
        </div>

        {/* Leaderboard Title */}
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
          {getModeLabel()}
        </h3>

        {/* Leaderboard Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mango-500"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={loadLeaderboard}
              className="mt-4 px-4 py-2 bg-mango-500 text-white rounded-lg hover:bg-mango-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <TrophyIcon className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              No leaderboard data yet. Be the first!
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {/* Top 3 with separator */}
              {entries.slice(0, 3).map((entry) => (
                <LeaderboardEntry
                  key={entry.user_id}
                  rank={entry.rank}
                  username={entry.username}
                  iconUrl={entry.icon_url}
                  score={entry.score}
                  achievedAt={entry.achieved_at}
                  isTopThree={entry.rank <= 3}
                  isCurrentUser={entry.user_id === currentUserId}
                  formatAsRating={mode === 'rating'}
                />
              ))}

              {/* Visual separator between top 3 and rest */}
              {entries.length > 3 && (
                <div className="border-t-2 border-gray-200 dark:border-gray-700 my-2"></div>
              )}

              {/* Rest of top 10 */}
              {entries.slice(3).map((entry) => (
                <LeaderboardEntry
                  key={entry.user_id}
                  rank={entry.rank}
                  username={entry.username}
                  iconUrl={entry.icon_url}
                  score={entry.score}
                  achievedAt={entry.achieved_at}
                  isTopThree={false}
                  isCurrentUser={entry.user_id === currentUserId}
                  formatAsRating={mode === 'rating'}
                />
              ))}
            </div>

            {/* User's position if outside top 10 */}
            {currentUserId && surroundingEntries.length > 0 && (
              <div className="mt-6 pt-4 border-t-2 border-mango-200 dark:border-mango-800">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Players Around You</p>
                <div className="space-y-3">
                  {surroundingEntries.map((entry) => (
                    <LeaderboardEntry
                      key={`${entry.user_id}-${entry.rank}`}
                      rank={entry.rank}
                      username={entry.username}
                      iconUrl={entry.icon_url}
                      score={entry.score}
                      achievedAt={entry.achieved_at}
                      isTopThree={false}
                      isCurrentUser={entry.user_id === currentUserId}
                      formatAsRating={mode === 'rating'}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderboardSection;
